/**
 * Resolve the `caseId` carried by a run into a human case title.
 *
 * Why this is not a plain `Map.get`. Runs do not all carry the same id
 * form. Mission scopes and harness tasks may carry either the registry
 * `caseId` (`cadeiras-ingest-slice`) or the flat-storage PATH SEGMENT
 * (`m-ingest-slice--slice`), which is `{slug}--{uid}` — the on-disk
 * folder name, not a registry key. Observed live in the sidebar: the
 * first form resolved to "Cadeiras Ingest Slice" while the second fell
 * through to a raw-id chip on an adjacent row.
 *
 * So the index is built on BOTH `caseId` and `slug`, and lookup falls
 * back to the slug portion before `--`.
 *
 * Pure + IO-free; the caller supplies the already-fetched case list.
 */

export interface CaseLabelSource {
  caseId: string;
  title: string;
  /** Flat-layout immutable path segment (entity-identity Slice 1). */
  slug?: string;
}

/** The trailing id segment D-4 appends to the folder slug — the part
 *  after the LAST `-` of the caseId (`case-mr8qmi6d-wsh72x` → `wsh72x`,
 *  `cadeiras-ingest-slice` → `slice`). */
export function caseIdTail(caseId: string): string {
  const cut = caseId.lastIndexOf("-");
  return cut >= 0 ? caseId.slice(cut + 1) : caseId;
}

/**
 * Build the lookup index once per case-list change.
 *
 * SLUG IS NOT A UNIQUE KEY. lexserver's `{orgId, slug}` index is
 * deliberately NON-unique (handback 2026-07-19) because the `--uid`
 * suffix is what disambiguates — two cases whose titles slugify the same
 * legitimately share a slug. An earlier version of this index mapped
 * `slug → title` first-writer-wins, which would have resolved a folder
 * segment to whichever case happened to be indexed first: a WRONG case
 * name on the row, which is worse than none. The risk is latent today
 * only because most rows still carry no slug; the identity backfill
 * populates them, and that is exactly when collisions start.
 *
 * So the index carries three key kinds:
 *   - the caseId (always exact);
 *   - the FULL folder segment `{slug}--{tail}` (exact — this is the D-4
 *     bridge and the one that matters for folder-shaped references);
 *   - the bare slug, but ONLY while it is unambiguous; a slug claimed by
 *     more than one case is poisoned so lookups refuse it.
 */
export function buildCaseLabelIndex(
  cases: readonly CaseLabelSource[] | undefined,
): Map<string, string> {
  const index = new Map<string, string>();
  const slugOwners = new Map<string, number>();
  for (const c of cases ?? []) {
    if (!c?.slug || !c.title) continue;
    slugOwners.set(c.slug, (slugOwners.get(c.slug) ?? 0) + 1);
  }
  for (const c of cases ?? []) {
    if (!c?.title) continue;
    if (c.slug && c.caseId) {
      // Exact folder segment — unambiguous even when the slug collides.
      index.set(`${c.slug}--${caseIdTail(c.caseId)}`, c.title);
    }
    // A contested slug resolves to nothing rather than to a coin flip.
    if (c.slug && slugOwners.get(c.slug) === 1 && !index.has(c.slug)) {
      index.set(c.slug, c.title);
    }
    if (c.caseId) index.set(c.caseId, c.title);
  }
  return index;
}

/** The `{slug}--{uid}` folder segment reduced to its slug. Returns the
 *  input unchanged when it carries no `--` separator. */
export function slugPartOf(caseId: string): string {
  const cut = caseId.indexOf("--");
  return cut > 0 ? caseId.slice(0, cut) : caseId;
}

/** Resolve a run's caseId to a title, or `undefined` when unknown.
 *
 *  Order matters: the caseId and the full `{slug}--{uid}` segment are
 *  both EXACT, so they are tried before the bare slug — which is only
 *  present in the index when exactly one case owns it. */
export function resolveCaseLabel(
  caseId: string | undefined,
  index: Map<string, string>,
): string | undefined {
  if (!caseId) return undefined;
  // Covers both a real caseId and a full folder segment.
  const direct = index.get(caseId);
  if (direct !== undefined) return direct;
  const slug = slugPartOf(caseId);
  return slug === caseId ? undefined : index.get(slug);
}

/**
 * NO FALLBACK LABEL EXISTS HERE — deliberately, and it must not be
 * reintroduced.
 *
 * An earlier revision rendered the slug portion of an unresolved id as
 * the case name. It looked reasonable and was strictly derived from real
 * data, which is exactly what made it harmful: it turned a DATA-INTEGRITY
 * FAULT into something that read as a slightly-terse case name, so the
 * pane appeared healthy while carrying an id that resolves to nothing.
 * That masking hid two live defects for an entire session (a mission
 * whose `scope.caseId` held a folder name, and a registry row missing the
 * `slug`/`storageShape` the caseId↔folder bridge depends on).
 *
 * When resolution fails the row must SAY SO — see `CaseChip`, which
 * renders a visibly abnormal "unresolved" state carrying the raw id.
 * Announcing failure is not the same as substituting for it.
 */
